import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BugReportIcon from '@mui/icons-material/BugReport';
import CampaignIcon from '@mui/icons-material/Campaign';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useNews } from '../../contexts/NewsContext';

// Map icon names to components
const iconMap = {
  AutoAwesome: AutoAwesomeIcon,
  TrendingUp: TrendingUpIcon,
  BugReport: BugReportIcon,
  Campaign: CampaignIcon,
};

// Map color names to MUI colors
const colorMap = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  primary: 'primary',
  error: 'error',
};

function NewsDialog({ open, onClose, news, allNews }) {
  const { t } = useTranslation();
  const { markAsRead } = useNews();
  const [selectedItem, setSelectedItem] = useState(null);

  // If allNews is provided, show list view first
  const showList = allNews && !selectedItem;
  const currentNews = selectedItem || news;

  const handleItemClick = async (item) => {
    setSelectedItem(item);
    if (!item.is_read) {
      await markAsRead(item.id);
    }
  };

  const handleBack = () => {
    setSelectedItem(null);
  };

  const handleClose = () => {
    setSelectedItem(null);
    onClose();
  };

  // Get icon component for category
  const getCategoryIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || CampaignIcon;
    return <IconComponent />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showList ? (
          <>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {t('news.allNews')}
            </Typography>
          </>
        ) : (
          <>
            {allNews && (
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ 
              color: `${colorMap[currentNews?.category_color] || 'primary'}.main`,
              display: 'flex',
              alignItems: 'center'
            }}>
              {getCategoryIcon(currentNews?.category_icon)}
            </Box>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {currentNews?.title}
            </Typography>
          </>
        )}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {showList ? (
          // List view
          <List>
            {allNews.map((item) => (
              <ListItem 
                key={item.id} 
                disablePadding
                sx={{
                  bgcolor: item.is_read ? 'transparent' : 'rgba(144, 202, 249, 0.08)',
                  mb: 1,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <ListItemButton onClick={() => handleItemClick(item)} sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 2 }}>
                    <Box sx={{ 
                      color: `${colorMap[item.category_color] || 'primary'}.main`,
                      mt: 0.5
                    }}>
                      {getCategoryIcon(item.category_icon)}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ fontWeight: item.is_read ? 400 : 600 }}
                        >
                          {item.title}
                        </Typography>
                        {!item.is_read && (
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main' 
                            }} 
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {item.summary || item.content?.substring(0, 150)}...
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={item.category_name} 
                          size="small" 
                          color={colorMap[item.category_color] || 'default'}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(item.published_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : currentNews ? (
          // Detail view
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Chip 
                label={currentNews.category_name} 
                size="small" 
                color={colorMap[currentNews.category_color] || 'default'}
              />
              <Typography variant="caption" color="text.secondary">
                {formatDate(currentNews.published_at)}
              </Typography>
            </Box>

            {currentNews.summary && (
              <Typography 
                variant="subtitle1" 
                color="text.secondary" 
                sx={{ mb: 3, fontStyle: 'italic' }}
              >
                {currentNews.summary}
              </Typography>
            )}

            <Box 
              sx={{ 
                '& h1': { typography: 'h4', mt: 3, mb: 2 },
                '& h2': { typography: 'h5', mt: 3, mb: 2 },
                '& h3': { typography: 'h6', mt: 2, mb: 1 },
                '& p': { typography: 'body1', mb: 2 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 0.5 },
                '& a': { color: 'primary.main' },
                '& code': { 
                  bgcolor: 'action.hover', 
                  px: 0.5, 
                  py: 0.25, 
                  borderRadius: 0.5,
                  fontFamily: 'monospace'
                },
                '& pre': { 
                  bgcolor: 'action.hover', 
                  p: 2, 
                  borderRadius: 1,
                  overflow: 'auto',
                  '& code': { bgcolor: 'transparent', p: 0 }
                },
                '& blockquote': {
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary'
                }
              }}
            >
              <ReactMarkdown>
                {currentNews.content}
              </ReactMarkdown>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewsDialog;
